import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchBeneficiaryTimelineProgress } from "../../api/subcontractorPortalApi";
import { useAppState } from "../../context/AppStateContext";
import { ComingSoonTabPanel } from "../ComingSoonTabPanel";
import { areMilestonesAndBillsUnavailable } from "../milestoneBillsGate";
import type { Application, ConstructionTimelinePhase, MilestoneTemplate } from "../../types";

function sortedTemplates(templates: MilestoneTemplate[]) {
  return [...templates].sort((a, b) => a.sortOrder - b.sortOrder);
}

type MilestoneStep = {
  tpl: MilestoneTemplate;
  prog: Application["milestoneProgress"][number];
  status: "complete" | "current" | "upcoming";
  hint: string;
};

function buildMilestoneSteps(templates: MilestoneTemplate[], app: Application): MilestoneStep[] {
  const rows: {
    tpl: MilestoneTemplate;
    prog: Application["milestoneProgress"][number];
  }[] = [];

  for (const tpl of templates) {
    const prog = app.milestoneProgress.find((p) => p.templateId === tpl.id);
    if (!prog) continue;
    rows.push({
      tpl,
      prog,
    });
  }

  const currentIdx = rows.findIndex((r) => !r.prog.milestoneCompletedAt);

  return rows.map((r, i) => {
    let status: MilestoneStep["status"];
    let hint: string;
    if (r.prog.milestoneCompletedAt) {
      status = "complete";
      hint = "Completed";
    } else if (i === currentIdx) {
      status = "current";
      hint = "Track subcontractor site updates in the timeline";
    } else {
      status = "upcoming";
      hint = "Not started";
    }
    return { ...r, status, hint };
  });
}

function maxIsoTimestamps(dates: (string | null | undefined)[]): string | null {
  const clean = dates.filter((d): d is string => Boolean(d && String(d).trim()));
  if (!clean.length) return null;
  return clean.reduce((a, b) => (a > b ? a : b));
}

function lastChecklistActivityIso(app: Application): string | null {
  const acc: string[] = [];
  for (const mp of app.milestoneProgress) {
    if (mp.milestoneCompletedAt) acc.push(mp.milestoneCompletedAt);
    for (const ip of mp.itemProgress) {
      if (ip.completedAt) acc.push(ip.completedAt);
    }
  }
  return maxIsoTimestamps(acc);
}

function formatTrackerDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "2-digit",
  });
}

function formatTrackerDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const datePart = formatTrackerDate(iso);
  const timePart = d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${datePart} — ${timePart}`;
}

function actualDaysForPhase(app: Application, phaseId: string): number | null {
  const raw = app.constructionTimelineActualDays?.[phaseId];
  return typeof raw === "number" && Number.isFinite(raw) && raw >= 0 ? raw : null;
}

/**
 * Map portal enum to officer-friendly label.
 * Work-progress only advances on `Approved`; `Submitted` is shown as
 * "Pending approval" so the Milestones tab matches the Work Progress Gantt.
 */
function portalStatusDisplay(status: string): { label: string; badgeClass: string; title: string } {
  switch (status) {
    case "Approved":
      return {
        label: "Approved",
        badgeClass: "badge badge--ok",
        title: "Portal: approved by office — counted in Work Progress",
      };
    case "Submitted":
      return {
        label: "Pending approval",
        badgeClass: "badge badge--pending",
        title: "Portal: subcontractor submitted — awaiting office approval. Work Progress will not advance until approved.",
      };
    case "Rejected":
      return { label: "Rejected", badgeClass: "badge badge--hold", title: "Portal: rejected" };
    case "Hold":
      return { label: "Hold", badgeClass: "badge badge--pending", title: "Portal: on hold" };
    default:
      return { label: "Pending", badgeClass: "badge badge--outline", title: "No subcontractor submission yet" };
  }
}

/** Build image src from portal LONGTEXT (data URL or raw base64). */
function portalPhotoSrc(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (s.startsWith("data:image/") || s.startsWith("data:application/octet-stream")) return s;
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  const compact = s.replace(/\s/g, "");
  if (compact.length < 32) return null;
  return `data:image/jpeg;base64,${compact}`;
}

function MilestoneTimelinePlanRow({
  phase,
  app,
  portalStatus,
  submittedAt,
  photoData,
}: {
  phase: ConstructionTimelinePhase;
  app: Application;
  portalStatus: string;
  submittedAt: string | null;
  photoData: string | null;
}) {
  const [open, setOpen] = useState(false);
  const actual = actualDaysForPhase(app, phase.id);
  const badge = portalStatusDisplay(portalStatus);
  const imgSrc = portalPhotoSrc(photoData);
  const hasSiteDetails = Boolean(submittedAt || imgSrc);

  return (
    <li className="milestone-vtimeline__plan-row">
      <div className="milestone-vtimeline__plan-cell milestone-vtimeline__plan-cell--main">
        <span className="milestone-vtimeline__plan-title">{phase.title}</span>
        <div className="milestone-vtimeline__plan-metrics">
          <span className="milestone-vtimeline__plan-plan">~{phase.approxDays} d plan</span>
          {actual != null ? (
            <span className="milestone-vtimeline__plan-actual"> · Logged {actual} d (site)</span>
          ) : (
            <span className="milestone-vtimeline__plan-actual milestone-vtimeline__plan-actual--muted">
              {" "}
              · No site log yet
            </span>
          )}
        </div>
        {hasSiteDetails ? (
          <div className="milestone-vtimeline__plan-actions">
            <button
              type="button"
              className="btn btn--link btn--sm milestone-vtimeline__view-btn"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
            >
              {open ? "Hide" : "View"}
            </button>
          </div>
        ) : null}
        {open && hasSiteDetails ? (
          <div className="milestone-vtimeline__plan-details">
            {submittedAt ? (
              <p className="milestone-vtimeline__plan-submitted text-muted">
                Subcontractor update: {formatTrackerDateTime(submittedAt)}
              </p>
            ) : null}
            {imgSrc ? (
              <div className="milestone-vtimeline__plan-photo-wrap">
                <img
                  src={imgSrc}
                  alt={`Site photo submitted for ${phase.title}`}
                  className="milestone-vtimeline__plan-photo"
                  loading="lazy"
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="milestone-vtimeline__plan-cell milestone-vtimeline__plan-cell--status">
        <span className={badge.badgeClass} title={badge.title}>
          {badge.label}
        </span>
      </div>
    </li>
  );
}

/** Flipkart-style summary card + expandable vertical “all updates” timeline. */
function resolveLinkedTimelines(
  tpl: MilestoneTemplate,
  constructionTimelinePhases: ConstructionTimelinePhase[],
): ConstructionTimelinePhase[] {
  return (tpl.linkedConstructionTimelinePhaseIds ?? [])
    .map((pid) => constructionTimelinePhases.find((p) => p.id === pid))
    .filter((p): p is ConstructionTimelinePhase => Boolean(p));
}

function MilestoneFlipkartTracker({
  templates,
  app,
  constructionTimelinePhases,
}: {
  templates: MilestoneTemplate[];
  app: Application;
  constructionTimelinePhases: ConstructionTimelinePhase[];
}) {
  const { refreshApplicationsFromApi } = useAppState();
  const [showAllUpdates, setShowAllUpdates] = useState(false);
  const [portalByPhaseId, setPortalByPhaseId] = useState<
    Record<string, { status: string; submitted_at: string | null; photo_data: string | null }>
  >({});
  const updatesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showAllUpdates) return;
    void refreshApplicationsFromApi();
  }, [showAllUpdates, refreshApplicationsFromApi]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchBeneficiaryTimelineProgress(app.id);
        if (cancelled) return;
        const m: Record<string, { status: string; submitted_at: string | null; photo_data: string | null }> =
          {};
        for (const r of rows) {
          m[String(r.timeline_phase_id)] = {
            status: r.status,
            submitted_at: r.submitted_at,
            photo_data: r.photo_data,
          };
        }
        setPortalByPhaseId(m);
      } catch {
        if (!cancelled) setPortalByPhaseId({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [app.id, showAllUpdates]);

  const steps = useMemo(() => buildMilestoneSteps(templates, app), [templates, app]);
  const current = steps.find((s) => s.status === "current");
  const allDone = steps.length > 0 && steps.every((s) => s.status === "complete");
  const lastActivity = lastChecklistActivityIso(app);
  const completionStamp = maxIsoTimestamps(steps.map((s) => s.prog.milestoneCompletedAt));

  const toggleUpdates = () => {
    setShowAllUpdates((prev) => {
      const next = !prev;
      if (next) {
        window.requestAnimationFrame(() => {
          updatesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
      return next;
    });
  };

  if (steps.length === 0) return null;

  return (
    <div className="milestone-flipkart-tracker" role="region" aria-label="Milestone status">
      <div className="milestone-track-hero card card--elevated">
        <div className="milestone-track-hero__top">
          <div className="milestone-track-hero__copy">
            {allDone ? (
              <p className="milestone-track-hero__status">
                Completed
                {completionStamp ? <>, {formatTrackerDate(completionStamp)}</> : null}
              </p>
            ) : (
              <p className="milestone-track-hero__status">
                In progress
                {current ? (
                  <>
                    , <span className="milestone-track-hero__status-phase">{current.tpl.title}</span>
                  </>
                ) : null}
              </p>
            )}
            <p className="milestone-track-hero__sub">
              {allDone
                ? "All milestone phases are complete for this beneficiary."
                : current
                  ? current.hint
                  : null}
            </p>
            <p className="milestone-track-hero__meta">
              <span className="milestone-track-hero__info" aria-hidden title="Information">
                ⓘ
              </span>
              <span>
                {lastActivity
                  ? `Last recorded update: ${formatTrackerDateTime(lastActivity)}`
                  : "No officer milestone timestamps recorded for this file yet."}
              </span>
            </p>
          </div>
          <div
            className={
              "milestone-track-hero__badge" +
              (allDone ? " milestone-track-hero__badge--done" : " milestone-track-hero__badge--active")
            }
            aria-hidden
          >
            {allDone ? "✓" : "●"}
          </div>
        </div>
        <hr className="milestone-track-hero__rule" />
        <button type="button" className="milestone-track-hero__updates-btn" onClick={toggleUpdates}>
          {showAllUpdates ? "Hide all updates" : "See all updates"}
        </button>
      </div>

      {showAllUpdates ? (
        <div
          ref={updatesRef}
          id="milestone-all-updates"
          className="milestone-vtimeline-wrap card card--elevated"
        >
          <h3 className="milestone-vtimeline-wrap__title">Milestone timeline</h3>
          <p className="milestone-vtimeline-wrap__lead text-muted">
            <strong>Plan</strong> comes from Timeline master. <strong>Logged days</strong>, <strong>status</strong>, and
            photos follow the subcontractor portal (same source as the Work Progress Gantt). Use{" "}
            <Link to={`/applications/${app.id}/bills`}>Bills</Link> and{" "}
            <Link to={`/applications/${app.id}/subcontractor`}>Subcontractor</Link> in the dossier for documents.
          </p>
          <ol className="milestone-vtimeline">
            {steps.map((s, i) => {
              const linkedTimelines = resolveLinkedTimelines(s.tpl, constructionTimelinePhases);
              return (
              <li key={s.tpl.id} className="milestone-vtimeline__row">
                <div className="milestone-vtimeline__rail" aria-hidden>
                  <span
                    className={
                      "milestone-vtimeline__dot milestone-vtimeline__dot--" + s.status
                    }
                  />
                  {i < steps.length - 1 ? (
                    <span
                      className={
                        "milestone-vtimeline__stem" +
                        (s.status === "complete" ? " milestone-vtimeline__stem--done" : "")
                      }
                    />
                  ) : null}
                </div>
                <div className="milestone-vtimeline__body">
                  <div className="milestone-vtimeline__head">
                    <strong className="milestone-vtimeline__phase-title">{s.tpl.title}</strong>
                    <span className="milestone-vtimeline__phase-date">
                      {s.status === "complete" && s.prog.milestoneCompletedAt
                        ? formatTrackerDate(s.prog.milestoneCompletedAt)
                        : s.status === "current"
                          ? "In progress"
                          : "Pending"}
                    </span>
                  </div>
                  {linkedTimelines.length > 0 ? (
                    <ul className="milestone-vtimeline__plans" aria-label="Construction timeline — plan vs site">
                      {linkedTimelines.map((p) => {
                        const portal = portalByPhaseId[String(p.id)];
                        return (
                          <MilestoneTimelinePlanRow
                            key={p.id}
                            phase={p}
                            app={app}
                            portalStatus={portal?.status ?? "Pending"}
                            submittedAt={portal?.submitted_at ?? null}
                            photoData={portal?.photo_data ?? null}
                          />
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="milestone-vtimeline__plans-empty text-muted">
                      No timeline levels linked for this milestone — edit links in{" "}
                      <Link to="/milestone-master">Milestone master</Link>.
                    </p>
                  )}
                </div>
              </li>
              );
            })}
          </ol>
        </div>
      ) : null}
    </div>
  );
}

export function MilestonesTab() {
  const { id } = useParams<{ id: string }>();
  const {
    getById,
    milestoneTemplates,
    constructionTimelinePhases,
    refreshApplicationsFromApi,
  } = useAppState();
  const app = id ? getById(id) : undefined;

  const orderedTemplates = useMemo(() => sortedTemplates(milestoneTemplates), [milestoneTemplates]);

  useEffect(() => {
    void refreshApplicationsFromApi();
  }, [id, refreshApplicationsFromApi]);

  if (!app) return null;

  if (areMilestonesAndBillsUnavailable(app)) {
    return (
      <ComingSoonTabPanel
        title="Milestones — coming soon"
        appId={app.id}
        body="Per-beneficiary milestone tracking is not available in this console yet — before or after subcontractor linkage. Your office will enable this step when the workflow is ready."
      />
    );
  }

  return (
    <>
      <p className="tab-intro">
        <strong>Milestone phases</strong> come from <strong>Milestone master</strong>. This tab shows status at a glance
        and the <strong>construction timeline</strong> tied to each phase, driven by{" "}
        <strong>subcontractor portal</strong> updates (same feed as Work Progress).
      </p>

      <div className="help-banner help-banner--info" style={{ marginBottom: "1rem" }}>
        <strong>Milestone &amp; timeline setup</strong>
        <p style={{ margin: "0.35rem 0 0" }}>
          Edit milestone phases and which construction levels they link to under{" "}
          <Link to="/milestone-master">Milestone master</Link>. Planned days come from{" "}
          <Link to="/construction-timeline-master">Timeline master</Link>.
        </p>
      </div>

      <MilestoneFlipkartTracker
        templates={orderedTemplates}
        app={app}
        constructionTimelinePhases={constructionTimelinePhases}
      />
    </>
  );
}
